import { useEffect, useRef, useState } from 'react';
import { AppBar, Box, Button, Container, GlobalStyles, Toolbar, Typography } from '@mui/material';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import VivaldiWoff from '../../assets/fonts/VIVALDII.woff';
import PublicFooter from './PublicFooter';
import { englishPublicConfig } from '../../utils/publicEnglish';

export default function PublicLayout() {
  useEffect(() => {
    const previousLanguage = document.documentElement.lang;
    document.documentElement.lang = 'en';
    return () => { document.documentElement.lang = previousLanguage; };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const panelOpen = isHome && ['#quienes-somos', '#contactanos', '#registro'].includes(location.hash);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(172);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const measure = () => setHeaderHeight(Math.ceil(header.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const [config, setConfig] = useState({
    site_title: 'Fresh Exports',
    site_subtitle: '',
    telefono_publico: '',
    whatsapp_publico: '',
    correo_publico: '',
    direccion_publica: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    footer_descripcion: '',
    footer_columna_titulo: '',
    footer_columna_texto: '',
    footer_boton_texto: '',
    footer_boton_link: '',
    footer_frase_final: '',
    whatsapp_boton_texto: ''
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data } = await api.get('/api/web/config');

        if (mounted && data) {
          setConfig((prev) => ({
            ...prev,
            ...englishPublicConfig(data)
          }));
        }
      } catch (error) {
        console.error('Error cargando configuración pública', error);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const scrollToSection = (sectionId) => {
    if (['quienes-somos', 'contactanos', 'registro'].includes(sectionId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = document.getElementById(sectionId);

    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace('#', '');

    const timer = setTimeout(() => {
      scrollToSection(id);
    }, 180);

    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' && !location.hash;
    return location.pathname.startsWith(path);
  };

  const isHashActive = (hash) => {
    return location.pathname === '/' && location.hash === hash;
  };

  const goToSection = (sectionId) => {
    const hash = `#${sectionId}`;

    if (location.pathname !== '/') {
      navigate({
        pathname: '/',
        hash
      });
      return;
    }

    if (location.hash !== hash) {
      navigate({
        pathname: '/',
        hash
      });
    }

    setTimeout(() => {
      scrollToSection(sectionId);
    }, 80);
  };

  return (
    <Box sx={{ '--public-header-height': `${headerHeight}px`, minHeight: '100vh', bgcolor: '#fff', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <GlobalStyles
        styles={`
          @font-face {
            font-family: 'VivaldiCustom';
            src: url(${VivaldiWoff}) format('woff');
            font-style: italic;
            font-weight: 400;
          }

          html {
            scroll-behavior: smooth;
          }

          body {
            margin: 0;
          }
        `}
      />

      <AppBar
        ref={headerRef}
        position={isHome ? (panelOpen ? 'fixed' : 'absolute') : 'sticky'}
        color="inherit"
        elevation={0}
        sx={{
          top: 0,
          borderTop: isHome ? '2px solid #d8b16a' : 'none',
          borderBottom: isHome ? 'none' : '1px solid #ececec',
          color: isHome ? '#fff' : '#111',
          bgcolor: isHome ? 'transparent' : 'rgba(255,255,255,.96)',
          backgroundImage: isHome
            ? 'linear-gradient(180deg, rgba(0,0,0,.48), transparent)'
            : 'none',
          backdropFilter: isHome ? 'none' : 'blur(14px)',
          '& .MuiButton-root:focus-visible, & a:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: 4
          }
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 140, sm: 120, md: 104 },
              py: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Typography
              component={RouterLink}
              to="/"
              sx={{
                textDecoration: 'none',
                color: isHome ? '#fff' : '#2f3542',
                textShadow: isHome ? '0 2px 12px rgba(0,0,0,.35)' : 'none',
                fontFamily: "'VivaldiCustom', serif",
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: { xs: 30, md: 40 },
                lineHeight: 1,
                letterSpacing: 0,
                whiteSpace: 'nowrap',
                display: 'inline-block'
              }}
            >
              {config.site_title || 'Fresh Exports'}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                '& .MuiButton-root': { fontSize: { xs: 11, sm: 14 } },
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-end' }
              }}
            >
              <Button
                component={RouterLink}
                to="/"
                color="inherit"
                sx={{
                  fontWeight: isActive('/') ? 700 : 500,
                  borderBottom: isActive('/') ? '2px solid currentColor' : '2px solid transparent',
                  borderRadius: 0
                }}
              >
                Home
              </Button>

              <Button
                onClick={() => goToSection('quienes-somos')}
                color="inherit"
                sx={{
                  fontWeight: isHashActive('#quienes-somos') ? 700 : 500,
                  borderBottom: isHashActive('#quienes-somos')
                    ? '2px solid currentColor'
                    : '2px solid transparent',
                  borderRadius: 0
                }}
              >
                About us
              </Button>

              <Button
                component={RouterLink}
                to="/catalogo"
                color="inherit"
                sx={{
                  fontWeight: isActive('/catalogo') ? 700 : 500,
                  borderBottom: isActive('/catalogo') ? '2px solid currentColor' : '2px solid transparent',
                  borderRadius: 0
                }}
              >
                Catalog
              </Button>

              <Button
                onClick={() => goToSection('registro')}
                color="inherit"
                sx={{ borderBottom: isHashActive('#registro') ? '2px solid currentColor' : '2px solid transparent', borderRadius: 0, fontWeight: isHashActive('#registro') ? 700 : 500 }}
              >
                Registration
              </Button>

              <Button
                onClick={() => goToSection('contactanos')}
                color="inherit"
                sx={{
                  fontWeight: isHashActive('#contactanos') ? 700 : 500,
                  borderBottom: isHashActive('#contactanos')
                    ? '2px solid currentColor'
                    : '2px solid transparent',
                  borderRadius: 0
                }}
              >
                Contact us
              </Button>

              <Button
                component={RouterLink}
                to="/sistema"
                variant="contained"
                sx={{
                  bgcolor: isHome ? 'transparent' : '#111',
                  color: '#fff',
                  border: isHome ? '1px solid rgba(255,255,255,.8)' : '1px solid transparent',
                  borderRadius: isHome ? 999 : 1,
                  boxShadow: isHome ? 'none' : undefined,
                  px: 3,
                  '&:hover': { bgcolor: isHome ? 'rgba(255,255,255,.16)' : '#333' }
                }}
              >
                Staff login
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      <PublicFooter config={config} />
    </Box>
  );
}
